import { Stack, Box, styled } from "@mui/material";
import { SmText } from "Components/Text";
import { useIntl } from "react-intl";
import { COLORS } from "constants/layout";
import { CrownIcon } from "Assets/icons";
import { VERIFIED2_ICON, UNVERIFIED_ICON } from "Assets/icons";
import { Text } from "Components/Text";
import { useMemo } from "react";
import Tooltip from "./Tooltip";
import { Img } from "Components/Img";

// Verification types
export enum VerificationType {
  WORK_EMAIL = "work_email",
  BUSINESS = "business",
  PHONE = "phone",
  IDENTITY = "identity",
  // Add more types as needed
}

// Verification level
export enum VerificationLevel {
  NONE = 0,
  BASIC = 1,
  STANDARD = 2,
  PREMIUM = 3,
  ENTERPRISE = 4,
}

// Base interfaces
export interface VerificationItem {
  label: string;
  show: boolean | number;
}

export interface VerificationStatusItem {
  type: VerificationType;
  isVerified: boolean;
  level?: VerificationLevel;
}

export interface VerificationConfig {
  title?: string;
  entity?: string;
  iconHeight?: number;
  customTooltipItems?: string[];
  verificationItems?: VerificationItem[];
  verificationStatuses: VerificationStatusItem[];
  customIcon?: {
    verified?: string;
    unverified?: string;
  };
  customTitle?: {
    verified?: string;
    unverified?: string;
  };
  // Custom verification rules
  verificationRules?: {
    minLevel?: VerificationLevel;
    requiredTypes?: VerificationType[];
  };
}

// Default configuration
const DEFAULT_CONFIG: Partial<VerificationConfig> = {
  entity: "Company",
  iconHeight: 12,
  customIcon: {
    verified: VERIFIED2_ICON,
    unverified: UNVERIFIED_ICON,
  },
  customTitle: {
    verified: "entity_is_verified_by_freshdi",
    unverified: "entity_is_not_verified",
  },
  verificationRules: {
    minLevel: VerificationLevel.BASIC,
    requiredTypes: [VerificationType.WORK_EMAIL, VerificationType.BUSINESS],
  },
};

// Component props extending the config
interface VerificationStatusProps
  extends Omit<VerificationConfig, "verificationStatuses"> {
  // New simple props
  is_featured?: number;
  is_verified?: number;
  data?: {
    // Verification fields
    is_verified_work_email?: number;
    is_biz_verified?: number; // Changed to number to match other verification fields
    is_verified_phone?: number;
    is_verified_identity?: number;
    verification_level?: number;
  };
}

const VerificationStatus = ({
  title,
  customTooltipItems = [],
  iconHeight,
  entity,
  customIcon,
  customTitle,
  verificationItems = [],
  verificationRules,
  // New simple props
  is_featured,
  is_verified,
  data,
}: VerificationStatusProps) => {
  const t = useIntl();

  // Convert verification props to VerificationStatus array
  const verificationStatuses = useMemo(() => {
    const statuses: VerificationStatusItem[] = [];

    // Handle simple props first
    if (typeof is_verified === "number") {
      statuses.push({
        type: VerificationType.BUSINESS,
        isVerified: Boolean(is_verified),
        level: data?.verification_level as VerificationLevel,
      });
    }

    if (data?.is_biz_verified) {
      statuses.push({
        type: VerificationType.BUSINESS,
        isVerified: true,
        level: data?.verification_level as VerificationLevel,
      });
    }

    // Handle existing props from data
    if (typeof data?.is_verified_work_email === "number") {
      statuses.push({
        type: VerificationType.WORK_EMAIL,
        isVerified: Boolean(data.is_verified_work_email),
        level: data?.verification_level as VerificationLevel,
      });
    }

    if (typeof data?.is_biz_verified === "number") {
      statuses.push({
        type: VerificationType.BUSINESS,
        isVerified: Boolean(data.is_biz_verified),
        level: data?.verification_level as VerificationLevel,
      });
    }

    if (typeof data?.is_verified_phone === "number") {
      statuses.push({
        type: VerificationType.PHONE,
        isVerified: Boolean(data.is_verified_phone),
        level: data?.verification_level as VerificationLevel,
      });
    }

    if (typeof data?.is_verified_identity === "number") {
      statuses.push({
        type: VerificationType.IDENTITY,
        isVerified: Boolean(data.is_verified_identity),
        level: data?.verification_level as VerificationLevel,
      });
    }

    return statuses;
  }, [
    data?.is_verified_work_email,
    data?.is_biz_verified,
    data?.is_verified_phone,
    data?.is_verified_identity,
    data?.verification_level,
    is_verified,
  ]);

  // Merge default config with props
  const config = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      iconHeight,
      entity,
      customIcon: {
        ...DEFAULT_CONFIG.customIcon,
        ...customIcon,
      },
      customTitle: {
        ...DEFAULT_CONFIG.customTitle,
        ...customTitle,
      },
      verificationRules: {
        ...DEFAULT_CONFIG.verificationRules,
        ...verificationRules,
      },
    }),
    [iconHeight, entity, customIcon, customTitle, verificationRules]
  );

  // Determine overall verification status
  const isVerified = useMemo(() => {
    // Consider verified if at least one verification type is verified
    const hasAnyVerification = verificationStatuses.some(
      (status) => status.isVerified
    );

    // If no verification level is required, just check for any verification
    if (!data?.verification_level) {
      return hasAnyVerification;
    }

    // If verification level exists, check both verification and level
    return (
      hasAnyVerification && data?.verification_level >= VerificationLevel.BASIC
    );
  }, [verificationStatuses, data?.verification_level]);

  // Generate verification details for tooltip
  const verificationDetails = useMemo(() => {
    const details: string[] = [];

    // Add verification types if verified
    if (isVerified) {
      // Add business verification if verified
      if (data?.is_biz_verified) {
        details.push('verified_business');
      }
      // Add work email verification if verified
      if (data?.is_verified_work_email) {
        details.push('verified_work_email');
      }
      // Add phone verification if verified
      if (data?.is_verified_phone) {
        details.push('verified_phone');
      }
      // Add identity verification if verified
      if (data?.is_verified_identity) {
        details.push('verified_identity');
      }

      // Add level information if verified and level exists
      if (data?.verification_level) {
        details.push(`verification_level_${data.verification_level}`);
      }
    }

    return details;
  }, [verificationStatuses, data?.verification_level, isVerified, data?.is_biz_verified, data?.is_verified_work_email, data?.is_verified_phone, data?.is_verified_identity]);

  // Get the title based on verification status
  const tooltipTitle = useMemo(() => {
    if (title) return title;
    const hasAnyVerification = verificationStatuses.some(
      (status) => status.isVerified
    );
    return hasAnyVerification
      ? t.formatMessage({ id: "buyer_is_verified_by_freshdi" })
      : config.customTitle?.unverified;
  }, [title, verificationStatuses, config.customTitle, t]);

  // Get the appropriate icon
  const icon = useMemo(() => {
    // If any verification exists, show verified icon
    const hasAnyVerification = verificationStatuses.some(
      (status) => status.isVerified
    );
    return hasAnyVerification
      ? config.customIcon?.verified
      : config.customIcon?.unverified;
  }, [verificationStatuses, config.customIcon]);

  return (
    <Stack direction="row" spacing={1}>
      <Tooltip
        title={
          <StyledTooltip>
            <Text>
              {t.formatMessage({ id: tooltipTitle }, { entity: config.entity })}
            </Text>
            {isVerified && verificationDetails.length > 0 && (
              <ul>
                {verificationDetails.map((detail, i) => (
                  <li key={i}>
                    <Text>{t.formatMessage({ id: detail })}</Text>
                  </li>
                ))}
              </ul>
            )}
            {customTooltipItems.length > 0 && (
              <ul>
                {customTooltipItems.map((item, i) => (
                  <li key={i}>
                    <Text>{t.formatMessage({ id: item })}</Text>
                  </li>
                ))}
              </ul>
            )}
          </StyledTooltip>
        }
      >
        <div>
          <Img
            src={icon}
            alt={
              verificationStatuses.some((status) => status.isVerified)
                ? "verified"
                : "unverified"
            }
            height={config.iconHeight}
            style={{
              width: 'auto',
              height: verificationStatuses.some((status) => status.isVerified) ? '12px' : `${config.iconHeight}px`,
              objectFit: 'contain'
            }}
          />
        </div>
      </Tooltip>
    </Stack>
  );
};

const StyledTooltip = styled(Box)({
  padding: 8,
  p: {
    fontSize: 10,
    "&::first-letter": {
      textTransform: "uppercase",
    },
  },
  ul: {
    paddingInlineStart: 10,
  },
  "& ul li p": {
    "&::first-letter": {
      textTransform: "uppercase",
    },
  },
});

export default VerificationStatus;
